import React, { createRef, memo, useMemo } from 'react';
import { CircleArrowLeft } from '../../assets/icons/CircleArrowLeft';
import { CircleArrowRight } from '../../assets/icons/CircleArrowRight';
import useDraggableScroll from '../../hooks/useDraggableScroll/useDraggableScroll';
import useScreenSize from '../../hooks/useScreenSize/useScreenSize';
import { ScreenSize } from '../../libraries/ScreenSizes';
import { getMergedClassNames } from '../../utils/HTMLutils';
import { scrollSmooth } from '../../utils/ScrollUtils';
import IconButton from '../iconButton/IconButton';
import TableUpperHeader, {
  TableUpperHeaderProps,
} from '../table/components/TableUpperHeader/TableUpperHeader';
import './TableWithOverflow.scss';
const rootClassName = 'component-table-with-overflow';

interface TableCell extends React.HTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'center' | 'right' | 'justify' | 'char';
}

export type ColumnObject = {
  key: string;
  props?: TableCell;
  value: any;
  absolute?: {
    left?: string;
    right?: string;
    width: string;
  };
};
interface TableWithOverflowProps {
  upperHeader?: TableUpperHeaderProps;
  showTopNavigator?: boolean;
  columns: ColumnObject[];
  lines?: any[];
  noResultMessage?: string;
}

function TableWithOverflow(props: TableWithOverflowProps) {
  const { rankedSize } = useScreenSize();
  const wrapperClassName = useMemo(() => {
    return getMergedClassNames([
      `${rootClassName}-wrapper`,
      props.upperHeader ? 'has-upper-header' : '',
      props.noResultMessage && !props.lines?.length ? 'no-result' : '',
      'left-columns-padding',
    ]);
  }, [props.upperHeader, props.lines, props.noResultMessage]);

  const outerTableRef = createRef<HTMLDivElement>();
  useDraggableScroll(outerTableRef, {
    mouseDownCondition: (el) =>
      rankedSize >= ScreenSize.md &&
      el.hasAttribute('data-draggable') &&
      el.getAttribute('data-draggable') === 'true',
  });

  const handleArrowClick = (toAdd = 0) => {
    if (outerTableRef.current) {
      const left = outerTableRef.current.scrollLeft;
      scrollSmooth({
        element: outerTableRef.current,
        top: 0,
        left: left + toAdd,
        behavior: 'smooth',
        duration: 400,
      });
    }
  };

  const someAbsoluteColumn = useMemo(() => {
    return props.columns.some((c) => c.absolute);
  }, [props.columns]);

  const atLeastOneStaticColumn = useMemo(() => {
    return props.columns.some((c) => !c.absolute);
  }, [props.columns]);

  const paddingLeft = useMemo(() => {
    return atLeastOneStaticColumn
      ? `${props.columns.reduce((prev, atual) => {
          const n = atual.absolute?.left ? parseInt(atual.absolute.width) : 0;
          return prev + n;
        }, 8)}px`
      : '8px';
  }, [props.columns, atLeastOneStaticColumn]);

  const paddingRight = useMemo(() => {
    return atLeastOneStaticColumn
      ? `${props.columns.reduce((prev, atual) => {
          const n = atual.absolute?.right ? parseInt(atual.absolute.width) : 0;
          return prev + n;
        }, 8)}px`
      : '8px';
  }, [props.columns, atLeastOneStaticColumn]);

  const columnsHeader = useMemo(() => {
    return props.columns?.map((column) => {
      const absoluteObj =
        atLeastOneStaticColumn && column.absolute ? column.absolute : {};
      return (
        <th
          key={`header-${column.key}-${atLeastOneStaticColumn}-${someAbsoluteColumn}`}
          data-draggable={
            someAbsoluteColumn && atLeastOneStaticColumn && !column.absolute
          }
          {...column.props}
          style={{ ...(column.props?.style ?? {}), ...absoluteObj }}
          className={getMergedClassNames([
            `${column.key}-cell`,
            atLeastOneStaticColumn && column.absolute ? 'absolute' : '',
          ])}
        >
          {column.value}
        </th>
      );
    });
  }, [props.columns, atLeastOneStaticColumn, someAbsoluteColumn]);

  const lines = useMemo(() => {
    return props.lines?.map((line, lineIndex) => {
      const k = line.key ? line.key : lineIndex;
      return (
        <tr key={k} aria-disabled={line.disabled || false}>
          <td data-draggable={false} className='absolute left-0' />
          {props.columns?.map((column) => {
            const absoluteObj =
              atLeastOneStaticColumn && column.absolute ? column.absolute : {};
            return (
              <td
                data-draggable={
                  someAbsoluteColumn &&
                  atLeastOneStaticColumn &&
                  !column.absolute
                }
                key={`${k}-${column.key}-${atLeastOneStaticColumn}-${someAbsoluteColumn}`}
                {...column.props}
                style={{
                  ...(column.props?.style ?? {}),
                  ...absoluteObj,
                }}
                className={getMergedClassNames([
                  column.props?.className,
                  `${column.key}-cell`,
                  atLeastOneStaticColumn && column.absolute ? 'absolute' : '',
                ])}
              >
                {line[column.key]}
              </td>
            );
          })}
          <td className='absolute right-0' />
        </tr>
      );
    });
  }, [props.lines, props.columns, atLeastOneStaticColumn, someAbsoluteColumn]);

  return (
    <div className={rootClassName}>
      {props.upperHeader ? (
        <div className={`${rootClassName}-upper-header`}>
          <TableUpperHeader {...props.upperHeader} />
        </div>
      ) : undefined}
      <div className={wrapperClassName} style={{ paddingLeft, paddingRight }}>
        {props.showTopNavigator && (
          <div className={`${rootClassName}-table-arrows`}>
            <IconButton
              icon={<CircleArrowLeft />}
              onClick={() => {
                handleArrowClick(-150);
              }}
            />
            <IconButton
              icon={<CircleArrowRight />}
              onClick={() => {
                handleArrowClick(150);
              }}
            />
          </div>
        )}
        <div className={`${rootClassName}-outer-table`} ref={outerTableRef}>
          <table>
            <thead>
              <tr>
                <th className='absolute left-0' />
                {columnsHeader}
                <th className='absolute right-0' />
              </tr>
              <tr />
            </thead>
            <tbody>{lines}</tbody>
          </table>
        </div>
        {props.noResultMessage && !props.lines?.length ? (
          <div className={`${rootClassName}-no-result`}>
            {props.noResultMessage}
          </div>
        ) : undefined}
      </div>
    </div>
  );
}

TableWithOverflow.defaultProps = {
  showTopNavigator: true,
};

export default memo(TableWithOverflow);
